<template>
    <div class="min-h-screen bg-gray-50 pb-20">
        <AppHeader />

        <div v-if="loading" class="container-custom mt-12 space-y-8 animate-pulse">
            <div class="h-64 bg-gray-200 rounded-3xl"></div>
            <div class="flex flex-col md:flex-row gap-8">
                <div class="w-full md:w-1/3 h-96 bg-gray-200 rounded-3xl"></div>
                <div class="w-full md:w-2/3 h-96 bg-gray-200 rounded-3xl"></div>
            </div>
        </div>

        <div v-else-if="!profile" class="container-custom mt-20 text-center py-20">
            <div class="max-w-md mx-auto">
                <svg class="w-20 h-20 text-gray-300 mx-auto mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9.172 9.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <h1 class="text-3xl font-bold text-gray-900 mb-4">Profile Not Found</h1>
                <p class="text-gray-600 mb-8">The developer profile you are looking for might have been moved or doesn't exist.</p>
                <router-link to="/about" class="btn btn-primary">Back to Team</router-link>
            </div>
        </div>

        <div v-else>
            <!-- Hero Section with Cover Image -->
            <section class="relative h-[450px] md:h-[400px] overflow-hidden">
                <div class="absolute inset-0">
                    <img v-if="profile.cover_image" :src="devStore.getImageUrl(profile.cover_image)"
                        class="w-full h-full object-cover" alt="Cover background" />
                    <div v-else
                        class="w-full h-full bg-gradient-to-br from-primary-600 via-purple-600 to-secondary-600"></div>
                    <div class="absolute inset-0 bg-black/40 backdrop-blur-[2px]"></div>
                </div>

                <div class="container-custom relative h-full flex items-center justify-center text-center md:items-end md:justify-start md:text-left pb-16 md:pb-12">
                    <div class="flex flex-col md:flex-row md:items-end gap-6 md:gap-10">
                        <!-- Profile Image -->
                        <div
                            class="w-28 h-28 xs:w-32 xs:h-32 md:w-48 md:h-48 rounded-[2rem] border-4 border-white shadow-2xl overflow-hidden bg-white relative z-10 -mb-12 md:-mb-10 transform hover:scale-105 transition-transform duration-500 mx-auto md:mx-0">
                            <img v-if="profile.profile_image" :src="devStore.getImageUrl(profile.profile_image)"
                                class="w-full h-full object-cover" :alt="profile.name" />
                            <div v-else
                                class="w-full h-full flex items-center justify-center text-primary-600 text-5xl font-bold">
                                {{ profile.name.charAt(0) }}</div>
                        </div>

                        <div class="mt-10 md:mt-0 md:mb-4 lg:mb-8 text-white">
                            <h1 class="text-3xl md:text-5xl font-bold tracking-tight mb-2 drop-shadow-lg">{{
                                profile.name }}</h1>
                            <p class="text-lg md:text-xl text-primary-100 font-medium   opacity-95 ">{{ profile.title }}</p>

                            <!-- Deployment Status -->
                            <!-- <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-white/20 backdrop-blur-md"
                                :class="profile.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'">
                                <span class="w-1.5 h-1.5 rounded-full animate-pulse" :class="profile.is_active ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.5)]' : 'bg-rose-400 shadow-[0_0_8px_rgba(251,113,133,0.5)]'"></span>
                                {{ profile.is_active ? 'Active Node' : 'Offline Node' }}
                            </div> -->
                        </div>
                    </div>
                </div>
            </section>

            <!-- Main Profile Content -->
            <div class="container-custom mt-20 md:mt-24">
                <div class="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">

                    <!-- Sidebar Info -->
                    <div class="space-y-8">
                        <!-- Glassy Stats Card -->
                        <div
                            class="bg-white/70 backdrop-blur-xl border border-white p-6 md:p-8 rounded-[2.5rem] shadow-xl shadow-gray-200/50">
                            <h3 class="text-xs font-bold text-gray-400 uppercase tracking-widest mb-6 md:mb-8 text-center">
                                Professional Stats</h3>
                            <div class="grid grid-cols-2 gap-4 md:gap-6">
                                <div
                                    class="text-center p-3 md:p-4 rounded-3xl bg-primary-50/50 border border-primary-100 hover:bg-primary-50 transition-colors">
                                    <p class="text-2xl md:text-3xl font-black text-primary-600">{{ profile.experience_years }}+</p>
                                    <p class="text-[9px] md:text-[10px] font-bold text-primary-800 uppercase mt-1">Years Exp
                                    </p>
                                </div>
                                <div
                                    class="text-center p-3 md:p-4 rounded-3xl bg-secondary-50/50 border border-secondary-100 hover:bg-secondary-50 transition-colors">
                                    <p class="text-2xl md:text-3xl font-black text-secondary-600">{{ profile.projects_completed }}+
                                    </p>
                                    <p class="text-[9px] md:text-[10px] font-bold text-secondary-800 uppercase mt-1">Projects Built
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Social Connectivity -->
                        <div class="bg-white p-6 md:p-8 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100">
                            <h3 class="text-sm font-bold text-gray-900 mb-6">Connect with {{ profile.name.split(' ')[0] }}</h3>
                            <div class="flex flex-col gap-3">
                                <a v-if="profile.social_links?.linkedin" :href="profile.social_links.linkedin"
                                    target="_blank"
                                    class="flex items-center justify-between p-4 bg-blue-50 text-blue-700 rounded-2xl hover:bg-blue-600 hover:text-white transition-all group">
                                    <span class="font-bold">LinkedIn</span>
                                    <svg class="w-5 h-5 transform group-hover:translate-x-1 transition-transform"
                                        fill="currentColor" viewBox="0 0 24 24">
                                        <path
                                            d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                                    </svg>
                                </a>
                                <a v-if="profile.social_links?.github" :href="profile.social_links.github"
                                    target="_blank"
                                    class="flex items-center justify-between p-4 bg-gray-50 text-gray-800 rounded-2xl hover:bg-gray-900 hover:text-white transition-all group">
                                    <span class="font-bold">GitHub</span>
                                    <svg class="w-5 h-5 transform group-hover:translate-x-1 transition-transform"
                                        fill="currentColor" viewBox="0 0 24 24">
                                        <path
                                            d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                                    </svg>
                                </a>
                                <a v-if="profile.social_links?.behance" :href="profile.social_links.behance"
                                    target="_blank"
                                    class="flex items-center justify-between p-4 bg-indigo-50 text-indigo-700 rounded-2xl hover:bg-indigo-600 hover:text-white transition-all group">
                                    <span class="font-bold">Behance</span>
                                    <svg class="w-5 h-5 transform group-hover:translate-x-1 transition-transform"
                                        fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M22.031 3V1h-6.562v2h6.562zM0 3v18h2.031v-1.168h3.31c.757 0 1.406.055 1.946.163.54.109.989.294 1.343.551.356.257.617.593.783 1.002.167.41.251.908.251 1.493 0 .576-.146 1.051-.436 1.426-.291.375-.687.678-1.187.909.508.209.944.492 1.309.844.364.351.652.769.861 1.25.209.481.33 1.022.33 1.636 0 .761-.108 1.436-.324 2.033-.217.596-.531 1.102-.934 1.516s-.916.728-1.53.946c-.614.218-1.33.328-2.131.328h-3.7V9.797h4.944c.54 0 .991.036 1.35.107.361.072.658.198.892.377.234.179.406.425.515.736.11.31.164.678.164 1.104 0 .426-.055.794-.164 1.104-.109.311-.281.557-.515.736-.234.179-.531.305-.892.377-.361.072-.81.107-1.35.107h-3.11V4.454h2.516c.558 0 1.032.034 1.425.1.392.066.711.192.958.378s.432.44.551.76c.119.32.18.711.18 1.173z"/>
                                    </svg>
                                </a>
                                <a v-if="profile.social_links?.dribbble" :href="profile.social_links.dribbble"
                                    target="_blank"
                                    class="flex items-center justify-between p-4 bg-rose-50 text-rose-600 rounded-2xl hover:bg-rose-500 hover:text-white transition-all group">
                                    <span class="font-bold">Dribbble</span>
                                    <svg class="w-5 h-5 transform group-hover:translate-x-1 transition-transform"
                                        fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 24C5.385 24 0 18.615 0 12S5.385 0 12 0s12 5.385 12 12-5.385 12-12 12zm0-22c-5.514 0-10 4.486-10 10s4.486 10 10 10 10-4.486 10-10-4.486-10-10-10z"/>
                                    </svg>
                                </a>
                                <a v-if="profile.social_links?.twitter" :href="profile.social_links.twitter"
                                    target="_blank"
                                    class="flex items-center justify-between p-4 bg-sky-50 text-sky-600 rounded-2xl hover:bg-sky-500 hover:text-white transition-all group">
                                    <span class="font-bold">Twitter</span>
                                    <svg class="w-5 h-5 transform group-hover:translate-x-1 transition-transform"
                                        fill="currentColor" viewBox="0 0 24 24">
                                        <path
                                            d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                                    </svg>
                                </a>
                            </div>

                            <div class="mt-8 pt-8 border-t border-gray-100 flex justify-center gap-6">
                                <a v-if="profile.social_links?.facebook" :href="profile.social_links.facebook"
                                    target="_blank" class="text-gray-400 hover:text-blue-600 transition-colors"><svg
                                        class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                        <path
                                            d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                                    </svg></a>
                                <a v-if="profile.social_links?.website" :href="profile.social_links.website"
                                    target="_blank" class="text-gray-400 hover:text-green-600 transition-colors"><svg
                                        class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9">
                                        </path>
                                    </svg></a>
                            </div>
                        </div>
                    </div>

                    <!-- Bio & Skills -->
                    <div class="lg:col-span-2 space-y-8 md:space-y-12">
                        <!-- Professional Bio -->
                        <div class="bg-white p-6 md:p-10 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100">
                            <h2 class="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8 flex items-center gap-3">
                                <span class="w-2 h-6 md:h-8 bg-primary-600 rounded-full"></span>
                                Professional Journey
                            </h2>
                            <div class="prose prose-base md:prose-lg text-gray-600 leading-relaxed whitespace-pre-wrap max-w-none">
                                {{ profile.bio || 'As a dedicated technical expert, I focus on delivering high-quality solutions that push the boundaries of modern web development and design.' }}
                            </div>
                        </div>

                        <!-- Skills Expertise -->
                        <div
                            class="bg-gradient-to-br from-primary-900 to-primary-800 p-6 md:p-10 rounded-[2.5rem] shadow-2xl relative overflow-hidden">
                            <!-- Decorative elements -->
                            <div class="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
                            <div
                                class="absolute -bottom-10 -left-10 w-24 h-24 bg-secondary-400/20 rounded-full blur-2xl">
                            </div>

                            <h2
                                class="font-heading text-2xl md:text-3xl font-bold text-white mb-8 md:mb-10 flex items-center gap-3 relative z-10">
                                <span class="w-2 h-6 md:h-8 bg-secondary-400 rounded-full"></span>
                                Technical Arsenal
                            </h2>

                            <div class="flex flex-wrap gap-3 md:gap-4 relative z-10">
                                <div v-for="skill in profile.skills" :key="skill"
                                    class="px-4 py-2 md:px-6 md:py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl text-white font-bold text-xs md:text-sm uppercase tracking-wider hover:bg-white/20 transition-all cursor-default">
                                    {{ skill }}
                                </div>
                            </div>

                            <div v-if="!profile.skills?.length" class="text-white/60 italic text-lg py-4">
                                Expertise in major modern technology stacks and frameworks.
                            </div>
                        </div>

                        <!-- Contact CTA -->
                        <div class="relative group">
                            <div
                                class="absolute -inset-1 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-[2.5rem] blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200">
                            </div>
                            <div
                                class="relative bg-white rounded-[2.5rem] p-6 md:p-10 flex flex-col md:flex-row items-center justify-between gap-8 border border-gray-100">
                                <div class="text-center md:text-left">
                                    <h3 class="text-xl md:text-2xl font-bold text-gray-900 mb-2">Interested in working with {{ profile.name.split(' ')[0] }}?</h3>
                                    <p class="text-gray-600">Get in touch to discuss your next big project and how we can help you succeed.</p>
                                </div>
                                <router-link to="/contact"
                                    class="w-full md:w-auto text-center px-8 py-4 bg-gray-900 text-white font-black rounded-2xl hover:bg-primary-600 transition-all transform hover:scale-105 active:scale-95 whitespace-nowrap shadow-xl">
                                    GET IN TOUCH
                                </router-link>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>

        <!-- Footer -->
        <AppFooter />
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useDevProfilesStore } from '@/stores/devProfiles'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppFooter from '@/components/layout/AppFooter.vue'

const route = useRoute()
const devStore = useDevProfilesStore()

const loading = ref(true)
const profile = computed(() => devStore.currentProfile)

onMounted(async () => {
    const slug = route.params.slug
    if (slug) {
        loading.value = true
        await devStore.fetchDevProfile(slug)
        loading.value = false
    }
})
</script>

<style scoped>
.container-custom {
    max-width: 80rem;
    margin-left: auto;
    margin-right: auto;
    padding-left: 1rem;
    padding-right: 1rem;
}

/* Glassmorphism utility */
.glass {
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.3);
}

.font-heading {
    font-family: 'Inter', sans-serif;
}
</style>
